"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BLOOM_THEME = void 0;
const common_1 = require("./common");
exports.BLOOM_THEME = (0, common_1.firstPartyTheme)({
    id: 'bloom',
    name: 'Bloom',
    tagline: 'Playful wellness: peach milk, berry and sage, soft shapes and a voice like a kind friend.',
    description: 'Bloom is a playful wellness system for yoga and pilates studios, therapists, nutrition coaches, supplements, skincare, period care and mindful apps. A peach-milk ground, berry primary, sage accent, the soft wonky curves of Fraunces over rounded Nunito text, big 24px corners and organic blob shapes. Warm, encouraging and inclusive without being saccharine.',
    categories: ['playful'],
    tokens: {
        colors: {
            background: '#fff6f1',
            surface: '#fce7dc',
            text: '#3a2a2f',
            muted: '#85707a',
            primary: '#c2477a',
            primaryText: '#ffffff',
            accent: '#7fb59a',
            border: '#f2d4c6',
        },
        fonts: { heading: 'Fraunces', body: 'Nunito' },
        radius: 24,
        density: 'comfortable',
        typeScale: 1.25,
    },
    guide: `## Mood
A sunny Sunday morning: oat latte, stretching on the floor, a friend who checks in on you. Soft, optimistic, body-positive and grounded. Playful through shape and voice, never through clutter. The visitor should exhale.

## Grid and layout
- Rounded everything: large 24px radius on images, cards and buttons (buttons are full pills). Organic blob shapes (SVG, surface or sage at low opacity) sit behind images and section headings, slightly offset.
- Flowing rather than boxed: alternate image-left and image-right splits, overlap images with blobs and small sticker-like badges ("Beginner friendly", "Vegan").
- Generous but cosy spacing; group related content in soft surface-coloured cards with no borders and no hard shadows (if any shadow, a very diffuse warm one: 0 20px 40px rgba(194, 71, 122, 0.08)).
- Wave or curved dividers between a couple of sections are welcome; use at most two per page.

## Typography
- Fraunces for headlines, weight 500–600, with its soft optical feel; italic Fraunces for an emphasised word or a gentle pull quote. Hero at the 4xl or 5xl step.
- Nunito for body at 16–18px, weight 400, line-height 1.7; UI and buttons in Nunito weight 700.
- Sentence case everywhere. Handwritten-feeling emphasis can come from a sage underline squiggle (SVG) under one word.

## Colour
- Peach milk background and blush surface carry the page.
- Berry is the action colour: primary buttons, links, the selected class or plan.
- Sage is the calm counterpoint: icons, tags, checkmarks, a blob behind a photo, success messages.
- Text is a warm plum-brown; never pure black. Keep contrast accessible: berry buttons always use white text.

## Imagery
Real, diverse bodies in natural light: movement, rest, food, hands holding mugs. Warm and slightly sun-washed grades. Product photography on peach or sage backgrounds with soft shadows. Simple line illustrations (single stroke, rounded caps) for icons and empty states. Avoid clinical white, gym-intensity imagery or before/after photos.

## Motion
Gentle and springy: 300–500ms with a soft spring (slight overshoot) for buttons and cards on hover (translateY -3px). Blobs drift slowly (20–30s loops, small amplitude). Elements fade up softly on scroll. Always honour prefers-reduced-motion.

## Copy tone
Warm, encouraging, inclusive, in second person: "Come as you are", "Five minutes counts", "Your pace, your practice". No guilt, no shame, no medical promises, no "burn", "fix" or "flawless". Short paragraphs, friendly microcopy on buttons ("Save my spot", "Start gently").

## Do
- Show schedules, class levels and prices clearly; calm design still needs clear booking.
- Use sticker badges for reassurance (Beginner friendly, First class free).
- Include a soft testimonial section with real names and photos.

## Don't
- No sharp corners, harsh black, neon or aggressive red.
- No dense data tables; use cards and chips.
- No fake urgency timers.`,
    sections: [
        {
            name: 'Hero: soft welcome',
            recipe: 'Split hero: left has a sage pill tag, a Fraunces headline (5xl) with one italic word underlined by a sage squiggle, a warm sentence, a berry pill button and a text link. Right has a rounded portrait photo (24px radius) overlapping a peach blob, with a small white sticker card floating on its corner ("4.9 ★ from 1,200 members"). Blob drifts slowly.',
        },
        {
            name: 'Class schedule cards',
            recipe: 'Horizontal day chips (pill buttons; the selected day in berry) above a list of rounded surface cards: time in Nunito bold, class name in Fraunces (xl), teacher avatar and name, level chip in sage, and a "Save my spot" berry pill. Cards lift 3px on hover with a soft spring.',
        },
        {
            name: 'Gentle benefits',
            recipe: 'Three rounded cards in a row, each with a single-stroke line illustration in sage inside a blob, a Fraunces heading (lg) and two kind sentences. On mobile they become a horizontal swipe row with snap points.',
        },
        {
            name: 'Kind words',
            recipe: "A testimonial carousel or masonry of rounded peach cards: a large Fraunces italic quote, the person's photo in a circle, first name and what they practise. A berry dot pager below. Only real testimonials provided by the owner.",
        },
    ],
});
